import { environmentStorageKey } from "@/lib/appEnvironment"
import { createStore, get, set } from "idb-keyval"
import { useAuthStore } from "@/store/authStore"

/**
 * Cache local (best-effort) de mídia JÁ enviada ao Azure, indexada pela própria URL
 * remota. Sem isso, retomar um preenchimento pausado sem conexão mostrava "Imagem
 * indisponível" pra qualquer foto/vídeo que já tinha sido enviado com sucesso antes da
 * pausa — o proxy autenticado (`useAuthenticatedImage`) precisa de rede pra buscar um
 * blob privado do Azure, mesmo que o arquivo já tenha passado por este mesmo aparelho.
 * `uploadBlob` (blobService.ts) grava aqui logo após todo upload bem-sucedido;
 * `useAuthenticatedImage` lê como fallback quando a busca ao vivo falha ou o aparelho
 * está offline.
 */
const cacheStore = createStore(environmentStorageKey("dkro-uploaded-media-cache-db"), "uploaded-media-cache")

export async function migrateVerifiedCachedBlob(url: string, userId: string) {
  if (useAuthStore.getState().user?.id !== userId || !/^https?:\/\//.test(url)) return
  if (await getCachedBlob(url, userId)) return
  const legacy = await get<Blob>(url, cacheStore)
  if (legacy?.size) await cacheUploadedBlob(url, legacy, userId)
}

export async function cacheUploadedBlob(url: string, blob: Blob, userId = useAuthStore.getState().user?.id): Promise<void> {
  try {
    if (!userId) return
    await set(`${userId}:${url}`, new Blob([blob], { type: typeof blob.type === "string" ? blob.type : "application/octet-stream" }), cacheStore)
  } catch {
    // Best-effort: falta de espaço/IndexedDB indisponível não pode quebrar o upload em si.
  }
}

export async function getCachedBlob(url: string, userId = useAuthStore.getState().user?.id): Promise<Blob | undefined> {
  try {
    if (!userId || useAuthStore.getState().user?.id !== userId) return undefined
    const blob = await get<Blob>(`${userId}:${url}`, cacheStore)
    return useAuthStore.getState().user?.id === userId && blob?.size ? blob : undefined
  } catch {
    return undefined
  }
}
